import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import { useConvexAuth } from 'convex/react'
import { useQuery, useMutation } from 'convex/react'
import { useEffect } from 'react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute('/invites/_blank/$id/confirm')({
  component: RouteComponent,
})

function RouteComponent() {
  const { id: inviteId } = Route.useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: isAuthLoading } = useConvexAuth()
  const inviteRedirectPath = `/invites/${inviteId}/confirm`

  // Once we're on the invite page, the redirect has been fulfilled.
  // Clear stale redirect keys to avoid / -> invite -> / loops.
  useEffect(() => {
    const pendingRedirect = sessionStorage.getItem('postAuthRedirectTo')
    if (pendingRedirect === inviteRedirectPath) {
      sessionStorage.removeItem('postAuthRedirectTo')
    }
  }, [inviteRedirectPath])

  const invite = useQuery(api.invites.getPublicInvite, {
    inviteId: inviteId as Id<'invites'>,
  })
  const profile = useQuery(
    api.profile.getMyProfile,
    isAuthenticated ? {} : 'skip',
  )

  const updateMyName = useMutation(api.profile.updateMyName)
  const acceptInviteFn = useMutation(api.invites.acceptInvite)
  const declineInviteFn = useMutation(api.invites.declineInvite)

  const form = useForm({
    defaultValues: {
      yourName: profile?.name ?? '',
    },
    onSubmit: async ({ value }) => {
      try {
        await updateMyName({ name: value.yourName.trim() })
        await acceptInviteFn({ inviteId: inviteId as Id<'invites'> })
        sessionStorage.removeItem('postAuthRedirectTo')
        toast.success(`You've joined ${invite?.orgName ?? 'the organization'}!`)
        window.location.href = '/'
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to accept invitation',
        )
      }
    },
  })

  const handleDecline = async () => {
    try {
      await declineInviteFn({ inviteId: inviteId as Id<'invites'> })
      toast.success('Invitation declined')
      void navigate({ to: '/sign-in' })
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to decline invitation',
      )
    }
  }

  const handleSignIn = () => {
    // Persist invite target so OAuth callback can route back here.
    sessionStorage.setItem('postAuthRedirectTo', inviteRedirectPath)
    void navigate({
      to: '/sign-in',
      search: { redirectTo: inviteRedirectPath },
    })
  }

  // Loading
  if (
    isAuthLoading ||
    invite === undefined ||
    (isAuthenticated && profile === undefined)
  ) {
    return (
      <InvitePageLayout>
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-3/4 mx-auto" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </InvitePageLayout>
    )
  }

  // Invalid / deleted invite
  if (invite === null) {
    return (
      <InvitePageLayout>
        <Card>
          <CardHeader>
            <CardTitle className="text-center text-2xl">
              Invite Not Found
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">
              This invitation link is invalid or has been removed.
            </p>
          </CardContent>
        </Card>
      </InvitePageLayout>
    )
  }

  // Expired
  if (invite.isExpired || invite.status === 'EXPIRED') {
    return (
      <InvitePageLayout>
        <Card>
          <CardHeader>
            <CardTitle className="text-center text-2xl">
              Invitation Expired
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">
              Your invitation to{' '}
              <span className="font-medium">{invite.orgName}</span> has expired.
              Please ask your administrator to send a new invite.
            </p>
          </CardContent>
        </Card>
      </InvitePageLayout>
    )
  }

  // Already accepted — but suppress during active submission to avoid the
  // reactive flicker when Convex updates invite.status before the redirect fires.
  if (invite.status === 'ACCEPTED' && !form.state.isSubmitting) {
    // If the current user is already a member, just redirect silently.
    if (isAuthenticated) {
      window.location.href = '/'
      return null
    }
    return (
      <InvitePageLayout>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Already Accepted</CardTitle>
            <CardDescription>
              You've already joined {invite.orgName}.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button
              onClick={() => {
                window.location.href = '/'
              }}
            >
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </InvitePageLayout>
    )
  }

  // Declined
  if (invite.status === 'DECLINED') {
    return (
      <InvitePageLayout>
        <Card>
          <CardHeader>
            <CardTitle className="text-center text-2xl">
              Invitation Declined
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">
              This invitation has been declined.
            </p>
          </CardContent>
        </Card>
      </InvitePageLayout>
    )
  }

  // PENDING — not signed in
  if (!isAuthenticated) {
    return (
      <InvitePageLayout>
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Join {invite.orgName}</CardTitle>
            <CardDescription>
              You've been invited to join as{' '}
              <span className="font-medium">{invite.email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-center text-muted-foreground">
              Sign in or create an account with{' '}
              <span className="font-medium">{invite.email}</span> to accept this
              invitation.
            </p>
            <Button className="w-full" size="lg" onClick={handleSignIn}>
              Sign In to Accept
            </Button>
          </CardContent>
        </Card>
      </InvitePageLayout>
    )
  }

  // PENDING — signed in
  return (
    <InvitePageLayout>
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Join {invite.orgName}</CardTitle>
          <CardDescription>
            You've been invited to join as{' '}
            <span className="font-medium">{invite.email}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="space-y-4"
          >
            <form.Field
              name="yourName"
              validators={{
                onChange: ({ value }) =>
                  !value || value.trim().length === 0
                    ? 'Your name is required'
                    : value.trim().length < 2
                      ? 'Name must be at least 2 characters'
                      : undefined,
              }}
            >
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="yourName">Your Name</Label>
                  <Input
                    id="yourName"
                    placeholder="Jane Smith"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    disabled={form.state.isSubmitting}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={form.state.isSubmitting}
            >
              {form.state.isSubmitting ? 'Joining...' : 'Accept Invitation'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={handleDecline}
              disabled={form.state.isSubmitting}
            >
              Decline
            </Button>
          </form>
        </CardContent>
      </Card>
    </InvitePageLayout>
  )
}

function InvitePageLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-full min-h-screen p-7 flex justify-center items-center bg-background">
      <div className="max-w-md w-full">{children}</div>
    </div>
  )
}
